import { useState } from 'react'
import { useBrandColors } from '../../hooks/useBrandColors'

const previousRequests = [
  { id: 'REQ-001', type: 'Feature Request', subject: 'Add split-shift to time clock', status: 'In Progress', priority: 'Standard', submitted: 'Apr 2, 2025', estimate: '$79', notes: 'GCW reviewing scope. ETA 3-5 business days.' },
  { id: 'REQ-002', type: 'Bug Fix', subject: 'Calendar week view not loading on Safari', status: 'Resolved', priority: 'Bug Fix', submitted: 'Mar 28, 2025', estimate: 'Free', notes: 'Fixed and deployed Mar 30.' },
  { id: 'REQ-003', type: 'Minor Change', subject: 'Rename Operations dept to Programs', status: 'Completed', priority: 'Minor', submitted: 'Mar 20, 2025', estimate: '$49', notes: 'Completed and live.' },
]

const requestTypes = [
  { key: 'bug', label: 'Bug Fix', price: 'Free', desc: 'Something is broken or not working as expected', color: '#4A7C59' },
  { key: 'minor', label: 'Minor Change', price: '$49', desc: 'Small UI tweak, label change, or field addition', color: '#4A6B8A' },
  { key: 'standard', label: 'Standard Feature', price: '$79-$99', desc: 'New form, report, modal, or moderate workflow', color: '#B8734A' },
  { key: 'major', label: 'Major Build', price: '$149-$199', desc: 'New module, complex workflow, multi-page feature', color: '#6B7D6B' },
  { key: 'integration', label: 'Custom Integration', price: 'Custom Quote', desc: 'API connection or third-party integration', color: '#2B2B2B' },
]

const modules = ['Dashboard', 'Employee Roster', 'Onboarding', 'Clock In/Out', 'Timesheets', 'Time Off', 'Memos', 'Document Library', 'Performance', 'Collective Services', 'Calendar', 'Client Config', 'Mobile / Navigation', 'Login / Auth', 'Other']

export default function DevRequests() {
  const { accent, accentBg } = useBrandColors()
  const [tab, setTab] = useState('new')
  const [submitted, setSubmitted] = useState(false)
  const [selectedType, setSelectedType] = useState('')
  const [form, setForm] = useState({ subject: '', module: '', description: '', businessReason: '', priority: 'normal', attachment: null })
  const [toast, setToast] = useState(null)

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 4000) }

  function handleSubmit() {
    if (!selectedType || !form.subject || !form.module || !form.description) {
      alert('Please fill in all required fields.')
      return
    }
    setSubmitted(true)
    setTimeout(() => {
      setSubmitted(false)
      setSelectedType('')
      setForm({ subject: '', module: '', description: '', businessReason: '', priority: 'normal', attachment: null })
      setTab('history')
      showToast('Request submitted! GCW team notified.')
    }, 3500)
  }

  const statusColor = { 'In Progress': '#C4843A', 'Resolved': '#4A7C59', 'Completed': '#4A7C59', 'Pending Review': '#4A6B8A', 'Quoted': '#B8734A' }

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>

      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 2000, background: '#2B2B2B', color: '#fff', padding: '12px 18px', borderRadius: 8, fontSize: 12, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#4A7C59" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4A7C59" strokeWidth="1.5" strokeLinecap="round"/></svg>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '1.4rem', color: '#2B2B2B', fontWeight: 600 }}>Dev Request Portal</h1>
          <p style={{ fontSize: 11, color: '#6B7D6B', marginTop: 2 }}>Submit bug reports and feature requests directly to the GCW development team</p>
        </div>
        <div style={{ padding: '10px 16px', background: accentBg, border: `1px solid ${accent}33`, borderRadius: 8, fontSize: 12 }}>
          <span style={{ fontWeight: 600, color: accent }}>Direct line to GCW</span>
          <span style={{ color: '#6B7D6B', marginLeft: 6 }}>info@gallaghercollectiveworks.com</span>
        </div>
      </div>

      {/* Pricing reference */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
        {requestTypes.map(rt => (
          <div key={rt.key} style={{ background: '#fff', border: `1px solid ${rt.color}22`, borderRadius: 8, padding: '10px 12px', borderTop: `3px solid ${rt.color}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: rt.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{rt.label}</div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 600, color: '#2B2B2B', marginBottom: 3 }}>{rt.price}</div>
            <div style={{ fontSize: 10, color: '#8A9E8A', lineHeight: 1.4 }}>{rt.desc}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#fff', border: '1px solid #EDE6DA', borderRadius: 8, overflow: 'hidden', marginBottom: 16, width: 'fit-content' }}>
        {[['new', 'Submit New Request'], ['history', 'Request History']].map(([key, label], i, arr) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '8px 24px', border: 'none', borderRight: i < arr.length - 1 ? '1px solid #EDE6DA' : 'none', background: tab === key ? accent : '#fff', color: tab === key ? '#fff' : '#4A4A4A', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s' }}>
            {label}
            {key === 'history' && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: tab === key ? 'rgba(255,255,255,0.25)' : accentBg, color: tab === key ? '#fff' : accent }}>{previousRequests.length}</span>}
          </button>
        ))}
      </div>

      {/* NEW REQUEST */}
      {tab === 'new' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14 }}>
          <div style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 10, overflow: 'hidden' }}>

            {submitted ? (
              <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(74,124,89,0.1)', border: '2px solid #4A7C59', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>✓</div>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 20, color: '#2B2B2B', fontWeight: 600, marginBottom: 8 }}>Request Submitted!</div>
                <div style={{ fontSize: 13, color: '#6B7D6B', lineHeight: 1.8 }}>
                  Your request has been sent to the GCW development team.<br/>
                  You will receive a response within 1-2 business days with pricing confirmation and timeline.
                </div>
              </div>
            ) : (
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Request type */}
                <div>
                  <label style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 8 }}>Request Type *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {requestTypes.map(rt => (
                      <button key={rt.key} onClick={() => setSelectedType(rt.key)}
                        style={{ padding: '10px 12px', border: `2px solid ${selectedType === rt.key ? rt.color : '#EDE6DA'}`, borderRadius: 8, background: selectedType === rt.key ? rt.color + '11' : '#fff', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", textAlign: 'left', transition: 'all 0.15s' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: selectedType === rt.key ? rt.color : '#2B2B2B', marginBottom: 2 }}>{rt.label}</div>
                        <div style={{ fontSize: 10, color: '#8A9E8A' }}>{rt.price}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject + Module */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5 }}>Subject / Title *</label>
                    <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief description of request..." style={{ width: '100%', padding: '9px 12px', border: '1px solid #EDE6DA', borderRadius: 6, fontSize: 12, fontFamily: "'Montserrat', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5 }}>Module Affected *</label>
                    <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))} style={{ width: '100%', padding: '9px 12px', border: '1px solid #EDE6DA', borderRadius: 6, fontSize: 12, fontFamily: "'Montserrat', sans-serif", outline: 'none', color: '#2B2B2B' }}>
                      <option value="">Select module...</option>
                      {modules.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5 }}>Description *</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} placeholder="Describe exactly what you need. The more detail the better — this helps us scope and price accurately..." style={{ width: '100%', padding: '9px 12px', border: '1px solid #EDE6DA', borderRadius: 6, fontSize: 12, fontFamily: "'Montserrat', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>

                {/* Business reason */}
                <div>
                  <label style={{ display: 'block', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5 }}>Business Reason (optional)</label>
                  <textarea value={form.businessReason} onChange={e => setForm(f => ({ ...f, businessReason: e.target.value }))} rows={2} placeholder="Why is this important to your team? This helps us prioritize..." style={{ width: '100%', padding: '9px 12px', border: '1px solid #EDE6DA', borderRadius: 6, fontSize: 12, fontFamily: "'Montserrat', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>

                {/* Priority + submit */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #EDE6DA', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ fontSize: 11, color: '#6B7D6B', fontWeight: 600 }}>Priority:</label>
                    {[['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent']].map(([val, label]) => (
                      <button key={val} onClick={() => setForm(f => ({ ...f, priority: val }))}
                        style={{ padding: '4px 12px', border: `1px solid ${form.priority === val ? accent : '#EDE6DA'}`, borderRadius: 20, background: form.priority === val ? accentBg : '#fff', color: form.priority === val ? accent : '#4A4A4A', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <button onClick={handleSubmit} style={{ padding: '10px 24px', background: accent, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
                    Submit to GCW →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 10, padding: 16 }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 14, fontWeight: 600, color: '#2B2B2B', marginBottom: 12 }}>What Happens Next</div>
              {[
                ['1', 'Submit your request', 'GCW team is notified immediately'],
                ['2', 'We review & scope', 'Response within 1-2 business days'],
                ['3', 'Price confirmation', 'You approve before any work begins'],
                ['4', 'We build it', 'Most requests completed in 1-5 days'],
                ['5', 'Live on your portal', 'Deployed with full testing'],
              ].map(([num, title, sub]) => (
                <div key={num} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{num}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#2B2B2B' }}>{title}</div>
                    <div style={{ fontSize: 11, color: '#8A9E8A' }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: accentBg, border: `1px solid ${accent}33`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: accent, marginBottom: 6 }}>Collective Suite clients</div>
              <div style={{ fontSize: 11, color: '#6B7D6B', lineHeight: 1.6 }}>Your first 3 feature requests are complimentary. Bug fixes are always free on all plans.</div>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY */}
      {tab === 'history' && (
        <div style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #EDE6DA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 600, color: '#2B2B2B' }}>Request History</div>
            <span style={{ fontSize: 11, color: '#6B7D6B' }}>{previousRequests.length} requests</span>
          </div>
          {previousRequests.map(r => (
            <div key={r.id} style={{ padding: '16px 20px', borderBottom: '1px solid #EDE6DA' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8A9E8A' }}>{r.id}</span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600, background: (statusColor[r.status] || '#8A9E8A') + '22', color: statusColor[r.status] || '#8A9E8A' }}>{r.status}</span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: '#F7F2EB', color: '#6B7D6B' }}>{r.type}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: accent }}>{r.estimate}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2B2B2B', marginBottom: 4 }}>{r.subject}</div>
              <div style={{ fontSize: 11, color: '#8A9E8A', marginBottom: 6 }}>Submitted {r.submitted}</div>
              {r.notes && (
                <div style={{ padding: '8px 12px', background: '#F7F2EB', borderRadius: 6, fontSize: 11, color: '#4A4A4A', borderLeft: `3px solid ${accent}` }}>{r.notes}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
